import React, { useEffect } from 'react';
import {
  CircularProgress,
  Alert,
} from '@mui/material';
import { type GridColDef } from '@mui/x-data-grid';
import { DataGrid, PageTitle, ListLayoutPanel, AddButton, GridEditButton } from '../../../components/common';
import { EditAsociacion } from './components';
import { type AsociacionListItem, type AsociacionRequest } from './types/asociacion';
import { useAsociacionStore } from './store';

const AsociacionPage: React.FC = () => {
  const {
    asociaciones,
    loading,
    error,
    fetchAsociaciones,
    saveAsociacion,
    clearError
  } = useAsociacionStore();

  // Local state for dialog management
  const [dialogOpen, setDialogOpen] = React.useState(false);
  const [selectedAsociacion, setSelectedAsociacion] = React.useState<AsociacionListItem | null>(null);

  const columns: GridColDef[] = [
    { field: 'descripcion', headerName: 'Descripción', width: 300, flex: 1 },
    { field: 'procedencia', headerName: 'Procedencia', width: 200 },
    {
      field: 'actions',
      headerName: 'Acciones',
      width: 100,
      sortable: false,
      filterable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <GridEditButton
          onClick={() => handleEdit(params.row.idAsociacion)}
          ariaLabel={`Editar asociación ${params.row.descripcion}`}
        />
      ),
    },
  ];

  useEffect(() => {
    fetchAsociaciones();
  }, [fetchAsociaciones]);

  const handleEdit = (idAsociacion: number) => {
    const asociacion = asociaciones.find(a => a.idAsociacion === idAsociacion) || null;
    setSelectedAsociacion(asociacion);
    setDialogOpen(true);
  };

  const handleCreate = () => {
    setSelectedAsociacion(null);
    setDialogOpen(true);
  };

  const handleClose = () => {
    setDialogOpen(false);
    setSelectedAsociacion(null);
  };

  const handleSave = async (updatedAsociacion: AsociacionRequest) => {
    try {
      await saveAsociacion(updatedAsociacion);
      handleClose();
    } catch (error) {
      console.error('Error saving asociacion:', error);
    }
  };

  if (loading) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Asociaciones"
          subtitle="Gestión de asociaciones"
          actions={
            <AddButton
              text="Nueva Asociación"
              onClick={handleCreate}
            />
          }
        />
        <CircularProgress />
      </ListLayoutPanel>
    );
  }

  if (error) {
    return (
      <ListLayoutPanel>
        <PageTitle
          title="Asociaciones"
          subtitle="Gestión de asociaciones"
          actions={
            <AddButton
              text="Nueva Asociación"
              onClick={handleCreate}
            />
          }
        />
        <Alert
          severity="error"
          onClose={clearError}
        >
          {error}
        </Alert>
      </ListLayoutPanel>
    );
  }

  return (
    <ListLayoutPanel>
      <PageTitle
        title="Asociaciones"
        subtitle="Gestión de asociaciones"
        actions={
          <AddButton
            text="Nueva Asociación"
            onClick={handleCreate}
          />
        }
      />

      <DataGrid
        columns={columns}
        rows={asociaciones}
        getRowId={(row: AsociacionListItem) => row.idAsociacion}
      />

      <EditAsociacion
        open={dialogOpen}
        asociacionId={selectedAsociacion?.idAsociacion || null}
        onClose={handleClose}
        onSave={handleSave}
        title={selectedAsociacion ? "Editar Asociación" : "Nueva Asociación"}
      />
    </ListLayoutPanel>
  );
};

export default AsociacionPage;